import React, { useContext, useState } from 'react';
import styles from '../../styles/cr.module.scss';
import { CrTextField } from '../cr/CrTextField';
import { CrLabel } from '../cr/CrLabel';
import { FormButtons } from '../cr/FormButtons';
import { IProject, IProjectUpdate, IPPAppProjectUpdateFinancial, ProjectUpdate, SaveStatus, IReportDueDates } from '../../types';
import { UpdateReportingStages } from '../../refData/UpdateReportingStages';
import { DataContext } from '../DataContext';
import useMobileDetector from '../useMobileDetector';
import { DateService } from '../../services';

export interface IProjectFinancialUpdateFormProps {
    project: IProject;
    projectUpdate: IProjectUpdate;
    reportDates: IReportDueDates;
    onSaved?: () => void;
    onCancelled?: () => void;
}

const getFYStartYear = (fyLabel: string): number => {
    const match = fyLabel?.match(/(\d{2})\/(\d{2})/);
    return match ? parseInt(match[1], 10) : 0;
};

const toNumberOrNull = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
};

export const ProjectFinancialUpdateForm = (props: IProjectFinancialUpdateFormProps): React.ReactElement => {
    const { project, projectUpdate, reportDates, onSaved, onCancelled } = props;
    const { dataServices } = useContext(DataContext);
    const isMobile = useMobileDetector();

    const initialSource = (projectUpdate?.PPAppProjectUpdateFinancials?.length > 0)
        ? projectUpdate.PPAppProjectUpdateFinancials
        : (project?.PPAppProjectFinancials || []);

    const sortedInitial = [...initialSource].sort(
        (a, b) => getFYStartYear(a.FYLabel) - getFYStartYear(b.FYLabel)
    );

    const fyLabels = sortedInitial.map(f => f.FYLabel);

    const [values, setValues] = useState<Record<string, number | null>>(() => {
        const map: Record<string, number | null> = {};
        sortedInitial.forEach(f => { map[f.FYLabel] = f.FYValue ?? null; });
        return map;
    });

    const [wholeLifeBenefit, setWholeLifeBenefit] = useState<number | null>(
        toNumberOrNull(projectUpdate?.WholeLifeBenefit) ?? toNumberOrNull(project?.TotalBenefits)
    );
    const [netPresentValue, setNetPresentValue] = useState<number | null>(
        toNumberOrNull(projectUpdate?.NetPresentValue) ?? toNumberOrNull(project?.NetPresentValue)
    );

    const [saveStatus, setSaveStatus] = useState<SaveStatus>(SaveStatus.None);
    const [saveError, setSaveError] = useState<string | null>(null);

    const rows: string[][] = [];
    for (let i = 0; i < fyLabels.length; i += 2) {
        rows.push(fyLabels.slice(i, i + 2));
    }

    const getDisplayValue = (fyLabel: string): string => {
        const v = values[fyLabel];
        return (v === null || v === undefined) ? '' : v.toLocaleString('en-GB');
    };

    const handleChange = (fyLabel: string, newValue: string): void => {
        const sanitized = newValue.replace(/[^0-9]/g, '');
        setValues(prev => ({ ...prev, [fyLabel]: sanitized === '' ? null : parseInt(sanitized, 10) }));
    };

    const getWholeLifeCost = (): number => Object.values(values).reduce((sum: number, v) => sum + (v || 0), 0);
    const getWholeLifeCostDisplay = (): string => getWholeLifeCost().toLocaleString('en-GB');

    const getNumberDisplayValue = (value: number | null): string =>
        (value === null || value === undefined) ? '' : value.toLocaleString('en-GB');

    const handleNumberChange = (setter: (v: number | null) => void, newValue: string): void => {
        const sanitized = newValue.replace(/[^0-9]/g, '');
        setter(sanitized === '' ? null : parseInt(sanitized, 10));
    };

    const renderFinancialField = (fyLabel: string) => (
        <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl6}`} key={fyLabel}>
            <CrLabel text={fyLabel} singleLine={true} />
            <CrTextField
                className={styles.formField}
                value={getDisplayValue(fyLabel)}
                onChange={(ev, newValue) => handleChange(fyLabel, newValue)}
            />
        </div>
    );

    const handleSave = async (): Promise<void> => {
        setSaveStatus(SaveStatus.Pending);
        try {
            // Base the payload on the existing update (if any) so we don't wipe out
            // RAG ratings/comments/etc already saved for this period — the backend
            // upserts by ProjectID+UpdatePeriod and overwrites every scalar field it receives.
            // const basePayload: IProjectUpdate = projectUpdate?.ID
            //     ? { ...projectUpdate }
            //     : new ProjectUpdate(project.ID, reportDates?.Next);

            const basePayload: IProjectUpdate = projectUpdate?.ID
                ? { ...projectUpdate, UpdatePeriod: DateService.setLocaleDateToUTC(new Date(projectUpdate.UpdatePeriod)) }
                : {
                    ...new ProjectUpdate(project.ID, reportDates?.Next),
                    ProjectPhaseID: project.ProjectPhaseID,
                    StartDate: project.StartDate,
                    EndDate: project.EndDate,
                };

            const updatedFinancials: IPPAppProjectUpdateFinancial[] = fyLabels.map(label => {
                const existing = projectUpdate?.PPAppProjectUpdateFinancials?.find(f => f.FYLabel === label);
                return { ...(existing || {}), FYLabel: label, FYValue: values[label] } as IPPAppProjectUpdateFinancial;
            });

            const payload: IProjectUpdate = {
                ...basePayload,
                WholeLifeCost: getWholeLifeCost(),
                WholeLifeBenefit: wholeLifeBenefit,
                NetPresentValue: netPresentValue,
                PPAppProjectUpdateFinancials: updatedFinancials,
                UpdateDate: new Date(),
                ReportingStageID: UpdateReportingStages.UpdatesInProgress,
            };

            delete payload.Project;
            delete (payload as unknown as { ID?: number }).ID;
            delete payload.UpdateUser;
            delete payload.OverallRagOption;
            delete payload.FinanceRagOption;
            delete payload.PeopleRagOption;
            delete payload.MilestonesRagOption;
            delete payload.BenefitsRagOption;
            delete payload.ProjectPhase;
            delete payload.BusinessCaseType;

            await dataServices.projectUpdateService.create(payload);
            setSaveStatus(SaveStatus.Success);
            onSaved?.();
        } catch (err) {
            setSaveStatus(SaveStatus.Error);
            setSaveError(err.message);
        }
    };

    return (
        <div className={styles.cr}>
            {fyLabels.length === 0 &&
                <div style={{ padding: '10px 12px', marginBottom: '15px', backgroundColor: '#f3f2f1', borderLeft: '3px solid #0078d4', color: '#605e5c', fontSize: '13px' }}>
                    No financial years are set up for this project yet.
                </div>
            }

            <div className={styles.grid}>
                {rows.map((row, idx) => (
                    <div className={styles.gridRow} key={`fin-row-${idx}`}>
                        {renderFinancialField(row[0])}
                        {row[1] && renderFinancialField(row[1])}
                    </div>
                ))}

                <div className={styles.gridRow}>
                    <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl6}`}>
                        <CrLabel text="Total Project Cost" singleLine={true} />
                        <CrTextField
                            className={styles.formField}
                            value={getWholeLifeCostDisplay()}
                            readonly={true}
                            styles={{ field: { backgroundColor: 'rgb(245, 245, 245)' } }}
                        />
                    </div>
                    <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl6}`}>
                        <CrLabel text="Total Benefits" singleLine={true} />
                        <CrTextField
                            className={styles.formField}
                            value={getNumberDisplayValue(wholeLifeBenefit)}
                            onChange={(ev, newValue) => handleNumberChange(setWholeLifeBenefit, newValue)}
                        />
                    </div>
                </div>

                <div className={styles.gridRow}>
                    <div className={`${styles.gridCol} ${styles.sm12} ${styles.xl6}`}>
                        <CrLabel text="Net Present Value" singleLine={true} />
                        <CrTextField
                            className={styles.formField}
                            value={getNumberDisplayValue(netPresentValue)}
                            onChange={(ev, newValue) => handleNumberChange(setNetPresentValue, newValue)}
                        />
                    </div>
                </div>
            </div>

            {saveStatus === SaveStatus.Error && saveError &&
                <div style={{ color: '#a4262c', marginBottom: '10px', fontSize: '13px' }}>{saveError}</div>
            }

            <FormButtons
                primaryText="Save"
                onPrimaryClick={handleSave}
                onSecondaryClick={() => onCancelled?.()}
                primaryStatus={saveStatus}
                primaryDisabled={saveStatus === SaveStatus.Pending}
                secondaryDisabled={false}
            />
        </div>
    );
};